import { useEffect, useRef, useState } from 'react';
import Markdown from './Markdown';

type Props = {
  text: string;
  // true 면 타이핑(스트리밍) 효과로 점진 노출, false 면 즉시 전체 표시(이력 등)
  animate: boolean;
  onStart?: () => void;
  onDone?: () => void;
  onProgress?: () => void;
};

// 답변 길이와 무관하게 전체 소요를 대략 일정(≈2초 이내)하게 유지하려고
// "틱 수"를 상한으로 두고 틱당 노출 글자 수를 길이에 맞춰 키운다.
const TICK_MS = 24;
const MAX_TICKS = 80;

/**
 * AI Chat 답변을 스트리밍처럼 한 글자씩 흘려 보여주는 렌더러.
 * 백엔드는 완성 답변 1건만 발행하므로(=토큰 스트림 없음) 프론트에서 타이핑 효과를 흉내낸다.
 * BE 가 추후 증분 청크를 발행하면 이 컴포넌트 대신 실시간 청크 누적으로 대체하면 된다.
 */
const StreamingMarkdown = ({
  text,
  animate,
  onStart,
  onDone,
  onProgress,
}: Props) => {
  const [shown, setShown] = useState(animate ? '' : text);
  // onDone 을 언마운트/완료에서 딱 한 번만 호출하기 위한 가드
  const doneRef = useRef(false);

  useEffect(() => {
    if (!animate) {
      setShown(text);
      return;
    }
    if (!text) return;

    onStart?.();
    doneRef.current = false;
    setShown('');

    const chunk = Math.max(1, Math.ceil(text.length / MAX_TICKS));
    let i = 0;
    let timer: ReturnType<typeof setTimeout>;

    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDone?.();
    };

    const step = () => {
      i = Math.min(text.length, i + chunk);
      setShown(text.slice(0, i));
      onProgress?.();
      if (i < text.length) {
        timer = setTimeout(step, TICK_MS);
      } else {
        finish();
      }
    };
    timer = setTimeout(step, TICK_MS);

    return () => {
      clearTimeout(timer);
      finish();
    };
    // onStart/onDone/onProgress 는 안정적(useCallback)이라 의존성에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animate]);

  return <Markdown>{shown}</Markdown>;
};

export default StreamingMarkdown;
